/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description application utils
 */
// todo
import _ from 'lodash';
import { KeycloakTokenParsed } from 'keycloak-js';

import { WEBSITE_URL } from 'configs/url';
import { USER_ROLES, ROLES_BY_PRIORITY, PAGE_ROLES } from 'configs/index';
import { APP_FEATURES } from 'configs/routes';
import { WORKFLOW_STATUS } from 'app-constants/workflow';

/**
 * parse user details from token
 */
const getUserFromToken = (tokenParsed?: KeycloakTokenParsed): App.User => {
	return {
		id: tokenParsed?.sub,
		email: tokenParsed?.email,
		username: tokenParsed?.preferred_username,
		name: tokenParsed?.name,
		firstName: tokenParsed?.given_name,
		lastName: tokenParsed?.family_name,
		displayName: _.startCase(tokenParsed?.name),
		realmRoles: tokenParsed?.resource_access?.veritable?.roles || [],
	};
};

/**
 * check intersection of valid roles
 */
const allowAccess = (userRoles: string[], roles: string[]): boolean => {
	return !_.isEmpty(_.intersection(userRoles, roles));
};

/**
 * Removes element from Dom
 */
const removeElementFromDom = (id = 'splash-screen'): void => {
	const initialLoader: HTMLElement | null = document.getElementById(id);
	if (initialLoader && initialLoader.parentNode) {
		initialLoader.parentNode.removeChild(initialLoader);
	}
};

/**
 * get user role
 */
const getUserRole = (userRoles: string[]): string => {
	return _.first(_.intersection(ROLES_BY_PRIORITY, userRoles));
};

/**
 * Check if the user should onboard
 * @param setupInfo - Setup Info
 * @returns boolean
 */
const shouldOnboard = (setupInfo: App.SetupInfo): boolean => {
	return !!setupInfo?.veritableOnboarding?.process_id && setupInfo?.veritableOnboarding?.status !== WORKFLOW_STATUS.completed;
};

/**
 * Check if the tenant has code explorer
 * @param setupInfo - Setup Info
 * @returns boolean
 */
const isCodeExplorerTenant = (setupInfo: App.SetupInfo): boolean => {
	return _.includes(setupInfo.selectedApps, APP_FEATURES.codeexplorer);
};

/**
 * Check if the code explorer is standalone
 * @param setupInfo - Setup Info
 * @returns boolean
 */
const isCodeExplorerStandalone = (setupInfo: App.SetupInfo): boolean => {
	return isCodeExplorerTenant(setupInfo) && shouldOnboard(setupInfo);
};

/**
 * App utils validateUserAccess
 */
const validateUserAccess = (userRoles: string[]): App.UserAccess => {
	const internalAdminRole = USER_ROLES.internalAdmin.role;
	const customerAdminRole = USER_ROLES.customerAdmin.role;
	const customerUserRole = USER_ROLES.customerUser.role;

	// app role
	const userRole = getUserRole(userRoles) || 'Guest';
	const canAccessApp = allowAccess(userRoles, [userRole]);
	const isInternalAdmin = allowAccess(userRoles, [internalAdminRole]);
	const isCustomerAdmin = !isInternalAdmin && allowAccess(userRoles, [customerAdminRole]);
	const isCustomerUser = !isInternalAdmin && !isCustomerAdmin && allowAccess(userRoles, [customerUserRole]);
	const canAccessAccounts = allowAccess(userRoles, PAGE_ROLES.settings.organization.subscriptions);

	// entity edit
	const canEditUsers = allowAccess(userRoles, PAGE_ROLES.settings.organization.users.edit);
	const canEditPayers = allowAccess(userRoles, PAGE_ROLES.settings.dataManagement.payers.edit);
	const canEditProviders = allowAccess(userRoles, PAGE_ROLES.settings.dataManagement.providers.edit);
	const canEditLocations = allowAccess(userRoles, PAGE_ROLES.settings.dataManagement.locations.edit);

	return {
		userRole,
		canAccessApp,
		isInternalAdmin,
		isCustomerAdmin,
		isCustomerUser,
		canAccessAccounts,

		canEditUsers,
		canEditPayers,
		canEditProviders,
		canEditLocations,
	};
};

/**
 * Set cookie
 */
const setCookie = (cname: string, cvalue: string, exdays: number, domain?: string): void => {
	const d = new Date();
	const baseDomain = domain ?? WEBSITE_URL;
	d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
	const expires = 'expires=' + d.toUTCString();
	document.cookie = cname + '=' + cvalue + ';' + expires + ';domain=' + baseDomain + '; path=/';
};

export {
	allowAccess,
	getUserFromToken,
	shouldOnboard,
	isCodeExplorerStandalone,
	isCodeExplorerTenant,
	removeElementFromDom,
	validateUserAccess,
	setCookie,
	getUserRole,
};
